import { format } from "date-fns";
import _ from "lodash";
import { v4 as uuidv4 } from "uuid";

import { createSlice } from "@reduxjs/toolkit";
import { query } from "@services/api";

import {
  RStatus,
  ReportSource,
  ReportStatus,
  TGlobalReports,
  TReportState,
} from "./types";

const initialState: TGlobalReports = {
  reportQueue: [],
  requestReport: null,
  filenames: [],
  reportTrayOpen: true,
};

// builds a unique url for the request
const buildUrl = (resource: string, params: Record<string, any>) =>
  resource + "?" + query({ ...params, uuid: uuidv4() });

const date = format(new Date(), "yyyy-MM-dd");
const createFilename = (reportName, filenames) => {
  let name: string;
  let i = 0;
  do {
    name = `${reportName}-${date}`;
    if (i > 0) name += `-v${i}`;
    i++;
  } while (filenames.includes(name));
  return name;
};

const reportSlice = createSlice({
  name: "globalReports",
  initialState,
  reducers: {
    requestReport(state, { payload: reportName }) {
      state.requestReport = reportName;
    },
    queueReport(state, action) {
      const {
        reportName: name,
        resource,
        params,
        source = ReportSource.CSV,
      } = action.payload;

      const url = buildUrl(resource, params);
      const filename = createFilename(name, state.filenames);
      const newReport: TReportState = {
        url,
        name,
        filename,
        id: uuidv4() as string,
        status: ReportStatus.LOADING,
        source,
      };
      state.reportQueue.unshift(newReport);
      state.filenames.push(filename);
      state.requestReport = null;
      state.reportTrayOpen = true;
    },
    setReportStatus(
      state,
      { payload: { id, status } }: { payload: { id: string; status: RStatus } }
    ) {
      state.reportQueue = state.reportQueue.map((rep) =>
        rep.id === id ? { ...rep, status } : rep
      );
    },
    clearReport(state, { payload: id }) {
      state.reportQueue = state.reportQueue.filter(
        (report) => report.id !== id
      );
    },
    clearAllReports(state) {
      state.reportQueue = [];
    },
    setReportTrayOpen(state, { payload: open }) {
      state.reportTrayOpen = open;
    },
  },
});

export const {
  requestReport,
  queueReport,
  setReportStatus,
  clearReport,
  clearAllReports,
  setReportTrayOpen,
} = reportSlice.actions;

export default reportSlice.reducer;
